import { useEffect, useMemo, useRef, useState } from 'react';

import useAuth from '@/hooks/useAuth';

import SendInvitationsModal from '../../components/modals/SendInvitationsModal';
import { ErrorText, MutedText } from '../../styles';
import { JobDraftV2 } from '../../context';
import { createOrderV2Client } from '../../api/client';
import {
  BillingAccountV2,
  CertificateV2,
  SkillListItem,
  WorkerCounts,
  WorkerListItem,
  WorkerSearchParams,
  WorkerTab,
} from '../../api/types';

import {
  ActionButtonsCell,
  AutoFillCard,
  AutoFillText,
  AvatarPlaceholder,
  BreakText,
  CertificateCard,
  CertificateChip,
  CertificateChipImage,
  CertificateChipsRow,
  CertificateHint,
  CertificateTitle,
  Checkbox,
  ChoiceBody,
  ChoiceCard,
  ChoiceDescription,
  ChoiceIcon,
  ChoiceRadio,
  ChoiceSection,
  ChoiceTitle,
  ConflictBadge,
  ContentWrap,
  DateCard,
  DateCardHeader,
  DateCardsRow,
  DateLabel,
  DowDot,
  DowRow,
  HeaderRow,
  InviteAvatar,
  InviteHeader,
  InvitedRow,
  PageButton,
  PaginationRow,
  PanelTitle,
  PanelTitleRow,
  SearchIconAbs,
  SearchInput,
  SearchWrap,
  SelectAllRow,
  SelectionPill,
  SelectionPillBadge,
  ShiftStatCell,
  ShiftsCard,
  ShiftsHeaderRow,
  StepContent,
  StepTitle,
  TabButton,
  TabsRow,
  TimeText,
  ToggleKnob,
  ToggleSwitch,
  WorkerAvatar,
  WorkerCell,
  WorkerMetaRow,
  WorkerName,
  WorkerPanel,
  WorkerRow,
  WorkerTextStack,
} from './styles';

type StaffingStrategy = NonNullable<JobDraftV2['staffing_strategy']>;

const WORKER_TABS: Array<{ label: string; value: WorkerTab }> = [
  { label: 'All', value: 'all' },
  { label: 'Favorites', value: 'favourites' },
  { label: 'Highly rated', value: 'highly_rated' },
  { label: 'Past workers', value: 'past_workers' },
];

const PAGE_SIZE = 15;

type StaffingStepProps = {
  orderType: 'GIG' | 'LTA' | null;
  billingAccount: BillingAccountV2 | null;
  jobs: JobDraftV2[];
  onUpdateJobs: (jobs: JobDraftV2[]) => void;
  skills?: SkillListItem[];
  // 'choice' renders the strategy cards only. 'invite' renders the shifts +
  // worker list shown after the user picks "Send Invitations" and clicks
  // "Continue to Invite". Parent owns this state so the wizard footer can
  // swap its button label/handler in lockstep.
  substep?: 'choice' | 'invite';
  // Pickable badge catalog for this billing account (mirrors legacy
  // `agency.certificates` filtered to BADGE type). Customer defaults are
  // already auto-seeded onto `jobs[0].certificates` by the parent.
  certificateOptions?: CertificateV2[];
};

// Mirrors v1's `workerName` helper (util/worker.ts): always includes the
// middle name when present, falling back to "Worker #<id>" if the user
// payload is missing entirely.
const formatName = (worker: WorkerListItem) => {
  const first = worker.user?.firstName || '';
  const middle = worker.user?.middleName || '';
  const last = worker.user?.lastName || '';
  const composed = [first, middle, last].filter(Boolean).join(' ').trim();
  return composed || `Worker #${worker.id}`;
};

const initialFor = (worker: WorkerListItem) => {
  const first = worker.user?.firstName?.trim() || '';
  const last = worker.user?.lastName?.trim() || '';
  return (first[0] || last[0] || '?').toUpperCase();
};

const isTruthyFlag = (v: unknown): boolean => v === true || v === 1;

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg
    viewBox="0 0 20 20"
    width={16}
    height={16}
    aria-hidden
    fill={filled ? '#FAAF00' : '#D1D5DB'}
  >
    <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.9L10 14.9 4.8 17.8l1-5.9L1.5 7.7l5.9-.9L10 1.5z" />
  </svg>
);

const FavoriteIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width={14}
    height={14}
    aria-label="Favorite worker"
    role="img"
  >
    <path
      d="M12 21s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 6.5a4.3 4.3 0 0 1 7.5 4.3C19.5 16.4 12 21 12 21z"
      fill="#45A735"
    />
  </svg>
);

const OpenJobIcon = () => (
  <svg viewBox="0 0 60 60" width={60} height={60} fill="none">
    <circle
      cx="30"
      cy="30"
      r="28"
      stroke="#44A735"
      strokeWidth="1.5"
      fill="#F5FFF4"
    />
    <path
      d="M20 22h20v18a4 4 0 0 1-4 4H24a4 4 0 0 1-4-4V22z"
      stroke="#44A735"
      strokeWidth="1.5"
      fill="#fff"
    />
    <path d="M26 22v-4a4 4 0 1 1 8 0v4" stroke="#44A735" strokeWidth="1.5" />
    <circle cx="30" cy="32" r="2" fill="#44A735" />
  </svg>
);

const InviteIcon = () => (
  <svg viewBox="0 0 60 60" width={60} height={60} fill="none">
    <circle
      cx="30"
      cy="30"
      r="28"
      stroke="#44A735"
      strokeWidth="1.5"
      fill="#F5FFF4"
    />
    <rect
      x="16"
      y="22"
      width="28"
      height="18"
      rx="2"
      stroke="#44A735"
      strokeWidth="1.5"
      fill="#fff"
    />
    <path d="M16 24l14 10 14-10" stroke="#44A735" strokeWidth="1.5" />
  </svg>
);

const SearchIconSvg = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ADADAD"
    strokeWidth={2}
    width={16}
    height={16}
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CheckmarkIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth={2.5}
    width={14}
    height={14}
  >
    <polyline points="4 12 9 17 20 7" />
  </svg>
);

const DashIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth={2.5}
    width={14}
    height={14}
  >
    <line x1="6" y1="12" x2="18" y2="12" />
  </svg>
);

const DOW_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;

const dowIndex = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return -1;
  // Date#getDay returns 0=Sunday, 6=Saturday — convert to 0=Mon ... 6=Sun.
  const day = d.getDay();
  return (day + 6) % 7;
};

const formatMonthDay = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatHm = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const StaffingStep = ({
  orderType,
  billingAccount,
  jobs,
  onUpdateJobs,
  skills,
  substep = 'choice',
  certificateOptions = [],
}: StaffingStepProps) => {
  const [tab, setTab] = useState<WorkerTab>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [workers, setWorkers] = useState<WorkerListItem[]>([]);
  const [counts, setCounts] = useState<WorkerCounts>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Indexes of shifts the user has deselected on the invite sub-screen.
  // Empty set = "All N shifts selected" (the prototype default).
  const [unselectedShifts, setUnselectedShifts] = useState<Set<number>>(
    () => new Set(),
  );
  // Cache of worker payloads keyed by id for any worker the admin has
  // invited or hired in this session. The wizard's flat invite_workers /
  // hire_workers arrays only carry ids; the cache lets the per-shift
  // "Invited" badge render an avatar/initial for each worker even after
  // the worker scrolls out of the paginated list.
  const invitedWorkersRef = useRef<Map<number, WorkerListItem>>(new Map());

  const { currentAdminIsCustomerAdmin: isClientAdmin } = useAuth();
  const isLta = orderType === 'LTA';
  const job = jobs[0];
  const strategy: StaffingStrategy | null = isLta
    ? 'SEND_INVITATIONS'
    : (job?.staffing_strategy ?? null);

  // Mirrors v1's `isLTA || allSchedulesArePast()` guard on the Hire button:
  // a Client Admin can only hire when the order is LTA or every shift has
  // already started. Otherwise the Hire column stays empty for that row.
  const allShiftsArePast = useMemo(() => {
    const list = job?.shifts || [];
    if (list.length === 0) return false;
    const now = Date.now();
    return list.every((shift) => {
      const t = new Date(shift.start_at).getTime();
      return Number.isFinite(t) && t < now;
    });
  }, [job?.shifts]);
  const canShowHireButton = !isClientAdmin || isLta || allShiftsArePast;

  const setStrategy = (next: StaffingStrategy) => {
    if (isLta) return;
    const nextJobs = [...jobs];
    nextJobs[0] = { ...nextJobs[0], staffing_strategy: next };
    onUpdateJobs(nextJobs);
  };

  const inviteIds = useMemo(
    () => job?.invite_workers || [],
    [job?.invite_workers],
  );
  const hireIds = useMemo(() => job?.hire_workers || [], [job?.hire_workers]);
  const certificateIds = useMemo(
    () => job?.certificates || [],
    [job?.certificates],
  );

  const toggleCertificate = (id: number) => {
    const next = certificateIds.includes(id)
      ? certificateIds.filter((existing) => existing !== id)
      : [...certificateIds, id];
    const nextJobs = [...jobs];
    nextJobs[0] = { ...nextJobs[0], certificates: next };
    onUpdateJobs(nextJobs);
  };

  const updateSelection = (changes: Partial<JobDraftV2>) => {
    const nextJobs = [...jobs];
    nextJobs[0] = { ...nextJobs[0], ...changes };
    onUpdateJobs(nextJobs);
  };

  // Mirrors v1's PRIVATE + publish:true semantics — persisted on the job
  // draft so `buildCreatePayload` can emit `publish` in the REST payload.
  const autoFill = job?.auto_fill === true;
  const setAutoFill = (next: boolean) => updateSelection({ auto_fill: next });

  const cacheWorker = (worker: WorkerListItem) => {
    invitedWorkersRef.current.set(worker.id, worker);
  };

  const toggleInvite = (worker: WorkerListItem) => {
    const id = worker.id;
    cacheWorker(worker);
    const next = inviteIds.includes(id)
      ? inviteIds.filter((w) => w !== id)
      : [...inviteIds, id];
    updateSelection({
      invite_workers: next,
      hire_workers: hireIds.filter((w) => w !== id),
    });
  };

  const toggleHire = (worker: WorkerListItem) => {
    const id = worker.id;
    cacheWorker(worker);
    const next = hireIds.includes(id)
      ? hireIds.filter((w) => w !== id)
      : [...hireIds, id];
    updateSelection({
      hire_workers: next,
      invite_workers: inviteIds.filter((w) => w !== id),
    });
  };

  const jobSchedulesParam = useMemo(() => {
    const shifts = (job?.shifts || []).map((shift) => ({
      dateRange: { startDate: shift.start_at, endDate: shift.start_at },
      startTime: shift.start_at.slice(11, 16),
      endTime: shift.end_at.slice(11, 16),
    }));
    return shifts.length > 0 ? JSON.stringify(shifts) : undefined;
  }, [job?.shifts]);

  const baseParams: WorkerSearchParams | null = useMemo(() => {
    if (!billingAccount) return null;
    return {
      billing_account_id: billingAccount.id,
      skill_id: job?.skill_id ?? null,
      address_id: job?.address_id ?? null,
      worker_type: isLta ? 'lta' : 'gig',
      tax_type: billingAccount.tax_type || 'all',
      background_check_required: !!billingAccount.require_background_checks,
      job_schedules: jobSchedulesParam,
      // Mirrors legacy `badgeIds` query param: when the admin has picked
      // certificate badges, restrict the worker list to workers that hold
      // them (only relevant on the invite sub-screen).
      badge_ids: certificateIds.length > 0 ? certificateIds : undefined,
    };
  }, [
    billingAccount,
    isLta,
    job?.skill_id,
    job?.address_id,
    jobSchedulesParam,
    certificateIds,
  ]);

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      250,
    );
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [
    tab,
    debouncedSearch,
    billingAccount?.id,
    job?.skill_id,
    job?.address_id,
  ]);

  // The worker list only renders on the invite sub-screen — the choice
  // screen shows the two strategy cards and nothing else, even after the
  // user has selected "Send Invitations".
  const showWorkerPanel =
    substep === 'invite' && strategy === 'SEND_INVITATIONS';

  useEffect(() => {
    if (!baseParams || !showWorkerPanel) {
      setWorkers([]);
      return;
    }
    setLoading(true);
    setError(null);
    let cancelled = false;
    createOrderV2Client
      .searchWorkers({
        ...baseParams,
        tab,
        search: debouncedSearch || undefined,
        page,
        per_page: PAGE_SIZE,
      })
      .then((result) => {
        if (cancelled) return;
        const list = result || [];
        // Seed the cache so the date-card invited avatars survive
        // pagination/tab/search changes (the next search may not return
        // the same worker on page 1).
        list.forEach((w) => invitedWorkersRef.current.set(w.id, w));
        setWorkers(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setError((err as Error).message || 'Failed to load workers');
        setWorkers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [baseParams, tab, debouncedSearch, page, showWorkerPanel]);

  useEffect(() => {
    if (!baseParams || !showWorkerPanel) {
      setCounts({});
      return;
    }
    let cancelled = false;
    createOrderV2Client
      .getWorkerCounts(baseParams)
      .then((result) => {
        if (!cancelled) setCounts(result || {});
      })
      .catch(() => {
        if (!cancelled) setCounts({});
      });
    return () => {
      cancelled = true;
    };
  }, [baseParams, showWorkerPanel]);

  const tabLabel = (value: WorkerTab, label: string) => {
    const map: Record<WorkerTab, number | undefined> = {
      all: counts.all ?? counts.others ?? undefined,
      favourites: counts.favourites ?? undefined,
      highly_rated: counts.highly_rated ?? undefined,
      past_workers: counts.past_workers ?? undefined,
    };
    const count = map[value];
    return typeof count === 'number' ? `${label} (${count})` : label;
  };

  const skillName =
    skills?.find((s) => s.id === job?.skill_id)?.display_name_en ||
    skills?.find((s) => s.id === job?.skill_id)?.name ||
    'workers';

  const renderWorkerRow = (worker: WorkerListItem) => {
    const inviteSelected = inviteIds.includes(worker.id);
    const hireSelected = hireIds.includes(worker.id);
    const conflict = !!worker.hasConflicts;
    const rating =
      typeof worker.averageRating === 'number' && worker.averageRating > 0
        ? worker.averageRating
        : null;
    const ratingCount =
      typeof worker.jobsRatingCount === 'number' && worker.jobsRatingCount > 0
        ? worker.jobsRatingCount
        : null;
    const skillShifts =
      typeof worker.selectedSkillShift === 'number'
        ? worker.selectedSkillShift
        : 0;
    const shiftsWithYou =
      typeof worker.shiftsWith === 'number' ? worker.shiftsWith : 0;

    // Mirrors v1 enable rules in StaffingWorkerProfileRow.tsx:
    //  - Hire visible only when worker.hireable && (!isClientAdmin || isLta || allShiftsArePast)
    //  - Invite visible only when worker.invitable && (!isLta || hasConflicts)
    const showHire = !!worker.hireable && canShowHireButton;
    const showInvite = worker.invitable !== false && (!isLta || conflict);

    return (
      <WorkerRow key={worker.id}>
        <WorkerCell>
          <WorkerAvatar aria-hidden>
            {worker.avatarUrl ? (
              <img
                src={worker.avatarUrl}
                alt=""
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <span>{initialFor(worker)}</span>
            )}
          </WorkerAvatar>
          <WorkerTextStack>
            <WorkerName title={formatName(worker)}>
              {formatName(worker)}
            </WorkerName>
            <WorkerMetaRow>
              <StarIcon filled={rating !== null} />
              <span style={{ color: rating !== null ? '#1C1C1C' : '#D1D5DB' }}>
                {rating !== null ? rating.toFixed(1) : '-'}
              </span>
              {ratingCount !== null ? <span>({ratingCount})</span> : null}
              {isTruthyFlag(worker.IsFavorite) ? (
                <>
                  <span style={{ color: '#D1D5DB' }}>|</span>
                  <FavoriteIcon />
                </>
              ) : null}
            </WorkerMetaRow>
          </WorkerTextStack>
        </WorkerCell>
        <ShiftStatCell>
          <strong>{skillShifts}</strong>
          {skillName ? `${skillName} Shifts` : 'Shifts'}
        </ShiftStatCell>
        <ShiftStatCell>
          <strong>{shiftsWithYou}</strong>
          Shifts With You
        </ShiftStatCell>
        <ActionButtonsCell>
          {conflict ? <ConflictBadge>Conflict</ConflictBadge> : null}
          {showHire ? (
            <SelectionPill
              type="button"
              state={hireSelected ? 'hired' : conflict ? 'disabled' : 'default'}
              disabled={conflict && !hireSelected}
              onClick={() => toggleHire(worker)}
            >
              {hireSelected ? 'Hired' : 'Hire'}
            </SelectionPill>
          ) : null}
          {showInvite ? (
            <SelectionPill
              type="button"
              state={
                inviteSelected
                  ? 'invited'
                  : hireSelected
                    ? 'disabled'
                    : 'default'
              }
              disabled={hireSelected}
              onClick={() => toggleInvite(worker)}
            >
              {inviteSelected ? 'Invited' : 'Invite'}
            </SelectionPill>
          ) : null}
        </ActionButtonsCell>
      </WorkerRow>
    );
  };

  const shifts = job?.shifts || [];
  const totalShifts = shifts.length;
  const selectedShiftCount = totalShifts - unselectedShifts.size;

  // Mirrors legacy `StaffingShiftsCardsSection`: each schedule card shows
  // the workers that have actually been invited (and hired) for it. V2
  // stores invite/hire ids flat on the job draft, but invitations are
  // scoped to the currently-selected shifts — so an unselected card
  // shows 0 Invited, matching the v1 schedule.selectedWorkers semantics.
  const invitedWorkersForCard = useMemo(() => {
    const cache = invitedWorkersRef.current;
    const lookup = (id: number): WorkerListItem =>
      cache.get(id) || ({ id } as WorkerListItem);
    return {
      invited: inviteIds.map(lookup),
      hired: hireIds.map(lookup),
    };
    // The cache is a ref so we need inviteIds/hireIds to refresh the memo.
  }, [inviteIds, hireIds]);
  const selectionState: 'none' | 'some' | 'all' =
    totalShifts === 0 || selectedShiftCount === 0
      ? 'none'
      : selectedShiftCount === totalShifts
        ? 'all'
        : 'some';

  const toggleShift = (index: number) => {
    setUnselectedShifts((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Tri-state header toggle:
  // - 'none' (no shifts selected): select every shift
  // - 'all' / 'some': deselect every shift
  // The 'some' branch matches the prototype where a partial selection still
  // shows "Deselect All" with a dash, and clicking clears the selection.
  const toggleSelectAll = () => {
    if (selectionState === 'none') {
      setUnselectedShifts(new Set());
      return;
    }
    const next = new Set<number>();
    for (let i = 0; i < totalShifts; i += 1) next.add(i);
    setUnselectedShifts(next);
  };

  const renderInviteScreen = () => {
    if (!billingAccount) {
      return <MutedText>Select a billing account to load workers.</MutedText>;
    }
    if (!job?.skill_id) {
      return (
        <MutedText>
          Pick a skill on Step 1 to filter the worker list — counts and rows
          update once a skill is selected.
        </MutedText>
      );
    }
    return (
      <>
        <InviteHeader>Invite for {skillName}</InviteHeader>

        <ShiftsCard>
          <ShiftsHeaderRow>
            <SelectAllRow type="button" onClick={toggleSelectAll}>
              <Checkbox
                checked={selectionState === 'all'}
                indeterminate={selectionState === 'some'}
              >
                {selectionState === 'all' ? (
                  <CheckmarkIcon />
                ) : selectionState === 'some' ? (
                  <DashIcon />
                ) : null}
              </Checkbox>
              <span>
                {selectionState === 'none' ? 'Select All' : 'Deselect All'}
              </span>
            </SelectAllRow>
            <SelectionPillBadge>
              {selectionState === 'all'
                ? `All ${totalShifts} shifts selected`
                : `${selectedShiftCount} of ${totalShifts} shifts selected`}
            </SelectionPillBadge>
          </ShiftsHeaderRow>

          <DateCardsRow>
            {shifts.length === 0 ? (
              <MutedText>No shifts scheduled.</MutedText>
            ) : (
              shifts.map((shift, index) => {
                const selected = !unselectedShifts.has(index);
                const activeDow = dowIndex(shift.start_at);
                return (
                  <DateCard key={index} selected={selected}>
                    <DateCardHeader>
                      <DateLabel>{formatMonthDay(shift.start_at)}</DateLabel>
                      <SelectAllRow
                        type="button"
                        onClick={() => toggleShift(index)}
                        aria-label={
                          selected ? 'Deselect shift' : 'Select shift'
                        }
                      >
                        <Checkbox checked={selected}>
                          {selected ? <CheckmarkIcon /> : null}
                        </Checkbox>
                      </SelectAllRow>
                    </DateCardHeader>
                    <DowRow>
                      {DOW_LETTERS.map((letter, idx) => (
                        <DowDot key={idx} active={idx === activeDow}>
                          {letter}
                        </DowDot>
                      ))}
                    </DowRow>
                    <div>
                      <TimeText>
                        {formatHm(shift.start_at)} - {formatHm(shift.end_at)}
                      </TimeText>
                      {shift.break_minutes ? (
                        <BreakText>{shift.break_minutes} mins break</BreakText>
                      ) : null}
                    </div>
                    <InvitedRow>
                      {(() => {
                        const invitedForCard = selected
                          ? invitedWorkersForCard.invited
                          : [];
                        const hiredForCard = selected
                          ? invitedWorkersForCard.hired
                          : [];
                        const label =
                          hiredForCard.length > 0
                            ? `${hiredForCard.length} Hired · ${invitedForCard.length} Invited`
                            : `${invitedForCard.length} Invited`;
                        const avatars = [
                          ...hiredForCard,
                          ...invitedForCard,
                        ].slice(0, 3);
                        return (
                          <>
                            <span aria-label="Invited workers count">
                              {label}
                            </span>
                            {avatars.length === 0 ? (
                              <AvatarPlaceholder aria-hidden />
                            ) : (
                              <span
                                aria-hidden
                                style={{ display: 'inline-flex', gap: 2 }}
                              >
                                {avatars.map((w) => (
                                  <InviteAvatar
                                    key={w.id}
                                    title={
                                      w.user?.firstName || `Worker #${w.id}`
                                    }
                                  >
                                    {w.avatarUrl ? (
                                      <img src={w.avatarUrl} alt="" />
                                    ) : (
                                      <span>{initialFor(w)}</span>
                                    )}
                                  </InviteAvatar>
                                ))}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </InvitedRow>
                  </DateCard>
                );
              })
            )}
          </DateCardsRow>
        </ShiftsCard>

        <WorkerPanel>
          <PanelTitleRow>
            <PanelTitle>All GravyWorkers</PanelTitle>
            <SearchWrap>
              <SearchIconAbs>
                <SearchIconSvg />
              </SearchIconAbs>
              <SearchInput
                aria-label="Search GravyWorkers"
                placeholder="Search for GravyWorkers"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </SearchWrap>
          </PanelTitleRow>
          <TabsRow>
            {WORKER_TABS.map(({ label, value }) => (
              <TabButton
                key={value}
                type="button"
                active={tab === value}
                onClick={() => setTab(value)}
              >
                {tabLabel(value, label)}
              </TabButton>
            ))}
          </TabsRow>
          <HeaderRow>
            <span>Worker Details</span>
            <span>{skillName ? `${skillName} Shifts` : 'Shifts'}</span>
            <span>Shifts With You</span>
            <span aria-hidden />
          </HeaderRow>
          {error ? (
            <ErrorText style={{ padding: '12px 24px' }}>{error}</ErrorText>
          ) : null}
          {loading && workers.length === 0 ? (
            <MutedText style={{ padding: '14px 24px' }}>
              Loading workers…
            </MutedText>
          ) : workers.length === 0 ? (
            <MutedText style={{ padding: '14px 24px' }}>
              No workers match this filter.
            </MutedText>
          ) : (
            workers.map(renderWorkerRow)
          )}
          <PaginationRow>
            <MutedText>
              {inviteIds.length} invited · {hireIds.length} hired
            </MutedText>
            <div style={{ display: 'flex', gap: 8 }}>
              <PageButton
                type="button"
                disabled={page === 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </PageButton>
              <PageButton
                type="button"
                disabled={loading || workers.length < PAGE_SIZE}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </PageButton>
            </div>
          </PaginationRow>
        </WorkerPanel>
      </>
    );
  };

  return (
    <StepContent>
      <ContentWrap wide={substep === 'invite'}>
        {substep === 'invite' ? (
          renderInviteScreen()
        ) : (
          <>
            <StepTitle>How would you like to staff your shifts?</StepTitle>

            {certificateOptions.length > 0 && (
              <CertificateCard>
                <CertificateTitle>Required badges</CertificateTitle>
                <CertificateHint>
                  Restrict invitations to workers that hold the selected badges.
                  Pre-selected badges come from this customer&apos;s defaults.
                </CertificateHint>
                <CertificateChipsRow>
                  {certificateOptions.map((cert) => {
                    const selected = certificateIds.includes(cert.id);
                    return (
                      <CertificateChip
                        key={cert.id}
                        type="button"
                        selected={selected}
                        aria-pressed={selected}
                        onClick={() => toggleCertificate(cert.id)}
                      >
                        {cert.image_url ? (
                          <CertificateChipImage
                            src={cert.image_url}
                            alt=""
                            aria-hidden
                          />
                        ) : null}
                        <span>{cert.name}</span>
                      </CertificateChip>
                    );
                  })}
                </CertificateChipsRow>
              </CertificateCard>
            )}

            {!isLta && (
              <ChoiceSection>
                <ChoiceCard
                  role="button"
                  tabIndex={0}
                  aria-pressed={strategy === 'OPEN_JOB_POST'}
                  selected={strategy === 'OPEN_JOB_POST'}
                  onClick={() => setStrategy('OPEN_JOB_POST')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setStrategy('OPEN_JOB_POST');
                    }
                  }}
                >
                  <ChoiceIcon>
                    <OpenJobIcon />
                  </ChoiceIcon>
                  <ChoiceBody>
                    <ChoiceTitle>Open job post</ChoiceTitle>
                    <ChoiceDescription>
                      Make your job publicly available to attract a wide pool of
                      qualified candidates.
                    </ChoiceDescription>
                  </ChoiceBody>
                  <ChoiceRadio selected={strategy === 'OPEN_JOB_POST'} />
                </ChoiceCard>

                <ChoiceCard
                  role="button"
                  tabIndex={0}
                  aria-pressed={strategy === 'SEND_INVITATIONS'}
                  selected={strategy === 'SEND_INVITATIONS'}
                  onClick={() => setStrategy('SEND_INVITATIONS')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setStrategy('SEND_INVITATIONS');
                    }
                  }}
                >
                  <ChoiceIcon>
                    <InviteIcon />
                  </ChoiceIcon>
                  <ChoiceBody>
                    <ChoiceTitle>Send invitations</ChoiceTitle>
                    <ChoiceDescription>
                      Send job invites to specific workers and ensure the best
                      fit for your needs.
                    </ChoiceDescription>
                    <AutoFillCard
                      onClick={(event) => event.stopPropagation()}
                      style={{ marginTop: 12 }}
                    >
                      <AutoFillText>
                        Make the job available to qualified workers if your
                        selected workers are unavailable.
                      </AutoFillText>
                      <ToggleSwitch
                        type="button"
                        on={autoFill}
                        onClick={(event) => {
                          event.stopPropagation();
                          setAutoFill(!autoFill);
                        }}
                        aria-pressed={autoFill}
                        aria-label="Make the job available to qualified workers if your selected workers are unavailable"
                      >
                        <ToggleKnob on={autoFill} />
                      </ToggleSwitch>
                    </AutoFillCard>
                  </ChoiceBody>
                  <ChoiceRadio selected={strategy === 'SEND_INVITATIONS'} />
                </ChoiceCard>
              </ChoiceSection>
            )}
          </>
        )}
      </ContentWrap>

      <SendInvitationsModal
        isOpen={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        onContinue={() => setInviteOpen(false)}
      />
    </StepContent>
  );
};

export default StaffingStep;
